import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import PlayerChoices from './components/PlayerChoices';
import TileInfo from './components/TileInfo';
import AuctionPanel from './components/AuctionPanel';
import ChatPanel from './components/ChatPanel';
import PropertyManagement from './components/PropertyManagement';
import GameTimer from './components/GameTimer';
import GameCompletedModal from './components/GameCompletedModal';
import EventsFeed from './components/EventsFeed';
import PlayerEliminatedModal from './components/PlayerEliminatedModal';
import EconomyStatus from './components/EconomyStatus';

import { useGameRenderer } from './hooks/useGameRenderer';

export default function Game() {
  const location = useLocation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const connectionRef = useRef(null);  
  // Get props from navigation state or localStorage
  const [userId, setUserId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [gameRoomId, setGameRoomId] = useState(null);
  
  const [camera, setCamera] = useState({ x: 0, y: 0, zoom: 1 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [selectedTile, setSelectedTile] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [shippingRoutes, setShippingRoutes] = useState({});
  const [landRoutes, setLandRoutes] = useState({});
  const [ships, setShips] = useState([]); // New state for active ships
  const [vans, setVans] = useState([]); // New state for active vans
  const [roadPaths, setRoadPaths] = useState({}); // New state for road paths
  const [chatMessages, setChatMessages] = useState([]); // New state for chat messages
  const [chatInput, setChatInput] = useState(''); // New state for chat input
  const [isChatExpanded, setIsChatExpanded] = useState(false); // New state for chat visibility
  const [bankDeposits, setBankDeposits] = useState({}); // New state for bank deposits
  const [bankInterestRates, setBankInterestRates] = useState({}); // New state for bank interest rates
  const [bankAvailableLoanAmounts, setBankAvailableLoanAmounts] = useState({}); // New state for bank available loan amounts
  const [bankInterestIncomes, setBankInterestIncomes] = useState({}); // New state for bank interest incomes
  const [playerLoans, setPlayerLoans] = useState([]); // New state for player loans
  const [allPlayerLoans, setAllPlayerLoans] = useState([]); // New state for all players' loans
  const [gameEvents, setGameEvents] = useState([]); // New state for game events
  const [isEventsExpanded, setIsEventsExpanded] = useState(false); // New state for events feed expansion
  const [tileUpgrades, setTileUpgrades] = useState({}); // State for tracking tile upgrades
  const [tradeAgreements, setTradeAgreements] = useState({}); // State for tracking trade agreements
  const [stockHoldings, setStockHoldings] = useState({}); // State for stock holdings
  const [isStockTradingEnabled, setIsStockTradingEnabled] = useState(false); // State for stock trading availability
  const [economyEffects, setEconomyEffects] = useState([]); // State for economy effects
  const [frog, setFrog] = useState(null); // New state for frog
  const [animationTime, setAnimationTime] = useState(0); // New state for animation timing
  const [equipmentUpgrades, setEquipmentUpgrades] = useState({}); // State for tracking equipment upgrades
  const [winningGoal, setWinningGoal] = useState(null); // New state for winning goal
  const [activeDebuffs, setActiveDebuffs] = useState({}); // State for active debuffs

  // Mobile layout state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileTab, setActiveMobileTab] = useState('actions'); // actions, events, chat

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [gameData, setGameData] = useState({
    gameRoomId: null,
    worldData: null,
    players: [],
    tileOwners: {},
    activeAuctions: {} // Changed from currentAuction to activeAuctions
  });
  const [auctionTimers, setAuctionTimers] = useState({}); // Track multiple auction timers
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');
  const [gameTimer, setGameTimer] = useState({
    startTime: null,
    endTime: null,
    isCompleted: false
  });
  const [preparationPeriod, setPreparationPeriod] = useState({
    isInPreparation: false,
    startTime: null,
    endTime: null
  });
  const [gameCompleted, setGameCompleted] = useState(null); // Store game completion data
  const [playerEliminated, setPlayerEliminated] = useState(null); // Store elimination data
  const [isSpectating, setIsSpectating] = useState(false); // Track spectator mode


  const TILE_SIZE = 32;

  // Initialize props from navigation state or localStorage
  useEffect(() => {
    if (location.state?.userId && location.state?.displayName && location.state?.gameRoomId) {
      setUserId(location.state.userId);
      setDisplayName(location.state.displayName);
      setGameRoomId(location.state.gameRoomId);
      // Store the game room ID for persistence
      localStorage.setItem('currentGameRoomId', location.state.gameRoomId.toString());
    } else {
      // Try to get from localStorage
      const savedUserId = localStorage.getItem('gameUserId');
      const savedDisplayName = localStorage.getItem('gameDisplayName');
      const savedGameRoomId = localStorage.getItem('currentGameRoomId');
      if (savedUserId && savedDisplayName) {
        setUserId(savedUserId);
        setDisplayName(savedDisplayName);
        if (savedGameRoomId) {
          setGameRoomId(parseInt(savedGameRoomId));
        }
        // We'll need to get current game room from SignalR only if no saved room
      } else {
        // No user data found, redirect to home
        navigate('/');
        return;
      }
    }
  }, [location, navigate]);

  // Setup SignalR connection
  useEffect(() => {
    if (!userId || !displayName) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl('https://api.paythefrog.io/api/gameHub')
      .build();

    connectionRef.current = connection;

    connection.start().then(() => {
      setConnectionStatus('Connected');
      
      // If we have a gameRoomId (from navigation or localStorage), join it
      if (gameRoomId) {
        connection.invoke('JoinGameRoom', gameRoomId, userId, displayName);
      } else {
        // Get current game room only if we don't have a saved one
        connection.invoke('GetCurrentGameRoom');
      }
    }).catch(err => {
      console.error('SignalR connection error:', err);
      setConnectionStatus('Connection Failed');
    });

    // Handle current game room info (only when we don't have gameRoomId)
    connection.on('CurrentGameRoom', (data) => {
      if (!gameRoomId) {
        setGameRoomId(data.gameRoomId);
        localStorage.setItem('currentGameRoomId', data.gameRoomId.toString());
        connection.invoke('JoinGameRoom', data.gameRoomId, userId, displayName);
      }
    });

    // Handle successful game join
    connection.on('GameJoined', (data) => {
      setGameData({
        gameRoomId: data.gameRoomId,
        worldData: data.world,
        players: data.players,
        tileOwners: data.tileOwners || {},
        activeAuctions: data.activeAuctions || {}
      });
      setShippingRoutes(data.shippingRoutes || {});
      setLandRoutes(data.landRoutes || {});
      setShips(data.ships || []);
      setVans(data.vans || []); // Set vans from server
      setRoadPaths(data.roadPaths || {}); // Set road paths from server
      setGameTimer(data.gameTimer || { startTime: null, endTime: null, isCompleted: false });
      setPreparationPeriod(data.preparationPeriod || { isInPreparation: false, startTime: null, endTime: null });
      
      // Set bank information from server
      if (data.bankInfo) {
        const interestRates = {};
        const availableLoanAmounts = {};
        
        Object.entries(data.bankInfo).forEach(([bankTileKey, bankData]) => {
          interestRates[bankTileKey] = bankData.interestRate;
          availableLoanAmounts[bankTileKey] = bankData.availableLoanAmount;
        });
        
        setBankInterestRates(interestRates);
        setBankAvailableLoanAmounts(availableLoanAmounts);
      }
      
      // Set player's active loans
      if (data.playerLoans) {
        setPlayerLoans(data.playerLoans);
      }
      
      // Set tile upgrades
      if (data.tileUpgrades) {
        setTileUpgrades(data.tileUpgrades);
      }
      
      // Set equipment upgrades
      if (data.equipmentUpgrades) {
        setEquipmentUpgrades(data.equipmentUpgrades);
      }
      
      // Set trade agreements
      if (data.tradeAgreements) {
        setTradeAgreements(data.tradeAgreements);
      }
      
      // Set stock holdings
      if (data.stockHoldings) {
        setStockHoldings(data.stockHoldings);
      }
      
      // Set stock trading status
      if (data.isStockTradingEnabled !== undefined) {
        setIsStockTradingEnabled(data.isStockTradingEnabled);
      }
      
      // Set economy effects
      if (data.economyEffects) {
        setEconomyEffects(data.economyEffects);
      }
      
      // Set chat messages history
      if (data.chatMessages) {
        setChatMessages(data.chatMessages.map(msg => ({
          id: msg.id,
          userId: msg.userId,
          displayName: msg.displayName,
          message: msg.message,
          timestamp: new Date(msg.timestamp),
          playerColor: msg.playerColor
        })));
      }
      
      // Set game events history from backend
      if (data.gameEvents) {
        setGameEvents(data.gameEvents.map(ev => ({
          id: ev.id,
          type: ev.type,
          playerId: ev.playerId,
          playerName: ev.playerName,
          borrowerName: ev.borrowerName,
          tileType: ev.tileType,
          startingBid: ev.startingBid,
          winningBid: ev.winningBid,
          operatingCostAmount: ev.operatingCostAmount,
          routeIncomeAmount: ev.routeIncomeAmount,
          loanAmount: ev.loanAmount,
          repayAmount: ev.repayAmount,
          interestAmount: ev.interestAmount,
          collectedAmount: ev.collectedAmount,
          interestRate: ev.interestRate,
          reason: ev.reason,
          tileKey: ev.tileKey,
          upgradeLevel: ev.upgradeLevel,
          upgradeCost: ev.upgradeCost,
          sellPrice: ev.sellPrice,
          agreementId: ev.agreementId,
          targetId: ev.targetId,
          targetName: ev.targetName,
          requesterName: ev.requesterName,
          partnerName: ev.partnerName,
          rejectorName: ev.rejectorName,
          cancellerName: ev.cancellerName,
          stockPrice: ev.stockPrice,
          stockCount: ev.stockCount,
          buybackPrice: ev.buybackPrice,
          remainingStocks: ev.remainingStocks,
          acquirerId: ev.acquirerId,
          acquirerName: ev.acquirerName,
          effectType: ev.effectType,
          effectPercentage: ev.effectPercentage,
          durationMinutes: ev.durationMinutes,
          tileTypeName: ev.tileTypeName,
          timestamp: new Date(ev.timestamp),
          isPersonal: ev.isPersonal || false,
          targetPlayerId: ev.targetPlayerId
        })));
      }
      
      // Set winning goal
      if (data.winningGoal) {
        setWinningGoal(data.winningGoal);
      }
      
      if (data.activeDebuffs) {
        setActiveDebuffs(data.activeDebuffs);
      }
      
      setConnectionStatus('In Game');
      localStorage.setItem('currentGameRoomId', data.gameRoomId.toString());
    });

    // Handle join errors
    connection.on('JoinGameError', (error) => {
      console.error('Join game error:', error);
      setConnectionStatus('Join Failed');
      if (error.currentActiveRoomId) {
        // Clear the old room ID and try to join the current active room
        localStorage.removeItem('currentGameRoomId');
        setGameRoomId(error.currentActiveRoomId);
        localStorage.setItem('currentGameRoomId', error.currentActiveRoomId.toString());
        connection.invoke('JoinGameRoom', error.currentActiveRoomId, userId, displayName);
      }
    });
    
    // Handle game already started - redirect to homepage
    connection.on('GameAlreadyStarted', (data) => {
      console.log('Game already started or ended:', data.message);
      alert(data.message);
      // Clear the stored game room and redirect to homepage
      localStorage.removeItem('currentGameRoomId');
      navigate('/');
    });
    
    // Handle player updates
    connection.on('PlayerJoined', (data) => {
      setGameData(prev => ({
        ...prev,
        players: data.players || prev.players
      }));
    });

    connection.on('ActiveDebuffsUpdated', (debuffs) => {
      setActiveDebuffs(debuffs || {});
    });

    connection.on('PlayersUpdated', (players) => {
      setGameData(prev => ({
        ...prev,
        players: players
      }));
    });

    // Handle auction events
    connection.on('AuctionStarted', (auction) => {
      setGameData(prev => ({
        ...prev,
        activeAuctions: {
          ...prev.activeAuctions,
          [auction.tileKey]: auction
        }
      }));
    });

    connection.on('BidPlaced', (bidData) => {
      setGameData(prev => ({
        ...prev,
        activeAuctions: {
          ...prev.activeAuctions,
          [bidData.tileKey]: {
            ...prev.activeAuctions[bidData.tileKey],
            ...bidData
          }
        }
      }));
    });

    connection.on('AuctionCompleted', (result) => {
      setGameData(prev => {
        const newActiveAuctions = { ...prev.activeAuctions };
        delete newActiveAuctions[result.tileKey];
        
        return {
          ...prev,
          activeAuctions: newActiveAuctions,
          tileOwners: {
            ...prev.tileOwners,
            [result.tileKey]: {
              owner: result.winner,
              purchasePrice: result.finalBid,
              purchasedAt: new Date().toISOString()
            }
          }
        };
      });
      
      // Clear selected tile if it was the one that was auctioned (to hide tile info)
      if (selectedTile && selectedTile.key === result.tileKey) {
        setSelectedTile(null);
      }
    });

    // Handle auctions updates (e.g., when auctions are cancelled due to player elimination)
    connection.on('AuctionsUpdated', (auctions) => {
      setGameData(prev => ({
        ...prev,
        activeAuctions: auctions
      }));
    });

    // Handle errors
    connection.on('Error', (message) => {
      console.error('Game error:', message);
    });

    connection.on('AuctionStartError', (message) => {
      console.error('Auction start error:', message);
    });

    connection.on('BidError', (message) => {
      console.error('Bid error:', message);
    });

    // Handle game state updates
    connection.on('GameStateUpdate', (data) => {
      setGameData({
        gameRoomId: data.gameRoomId,
        worldData: data.world,
        players: data.players,
        tileOwners: data.tileOwners || {},
        activeAuctions: data.activeAuctions || {} // Changed from currentAuction
      });
      setShippingRoutes(data.shippingRoutes || {});
      setLandRoutes(data.landRoutes || {});
      setShips(data.ships || []);
      setVans(data.vans || []); // Update vans from server
      setRoadPaths(data.roadPaths || {}); // Update road paths from server
      
      // Update bank information from server
      if (data.bankInfo) {
        const interestRates = {};
        const availableLoanAmounts = {};
        
        Object.entries(data.bankInfo).forEach(([bankTileKey, bankData]) => {
          interestRates[bankTileKey] = bankData.interestRate;
          availableLoanAmounts[bankTileKey] = bankData.availableLoanAmount;
        });
        
        setBankInterestRates(interestRates);
        setBankAvailableLoanAmounts(availableLoanAmounts);
      }
      
      if (data.activeDebuffs) {
        setActiveDebuffs(data.activeDebuffs);
      }
    });

    // Handle shipping route updates
    connection.on('ShippingRoutesUpdated', (routes) => {
      setShippingRoutes(routes);
    });

    // Handle land route updates
    connection.on('LandRoutesUpdated', (routes) => {
      setLandRoutes(routes);
    });

    // Handle road path updates
    connection.on('RoadPathsUpdated', (paths) => {
      setRoadPaths(paths);
    });

    // Handle ship updates
    connection.on('ShipsUpdated', (shipsData) => {
      setShips(shipsData);
    });

    // Handle van updates
    connection.on('VansUpdated', (vansData) => {
      setVans(vansData);
    });

    // Handle chat messages
    connection.on('ChatMessageReceived', (chatData) => {
      setChatMessages(prev => [...prev, {
        id: chatData.id, // Use server-provided ID
        userId: chatData.userId,
        displayName: chatData.displayName,
        message: chatData.message,
        timestamp: new Date(chatData.timestamp),
        playerColor: chatData.playerColor
      }]);
    });

    connection.on('ChatError', (error) => {
      console.error('Chat error:', error);
    });

    // Handle preparation timer updates
    connection.on('PreparationTimerUpdate', (timerData) => {
      setPreparationPeriod({
        isInPreparation: true,
        remainingSeconds: timerData.remainingSeconds
      });
    });

    // Handle game start (end of preparation period)
    connection.on('GameStarted', (startData) => {
      setPreparationPeriod({
        isInPreparation: false,
        startTime: null,
        endTime: null
      });
      setGameTimer(startData.gameTimer || { startTime: null, endTime: null, isCompleted: false });
      // Show a brief notification that the game has started
      console.log(startData.message);
    });

    // Handle game completion
    connection.on('GameCompleted', (completionData) => {
      setGameCompleted(completionData);
      setGameTimer(prev => ({
        ...prev,
        isCompleted: true
      }));
    });

    // Handle player elimination
    connection.on('PlayerEliminated', (eliminationData) => {
      // Check if the current user was eliminated
      if (eliminationData.eliminatedPlayerId === userId) {
        setPlayerEliminated({
          eliminatedBy: eliminationData.acquirerName,
          isBankruptcy: eliminationData.isBankruptcy || false
        });
      }
    });

    // Handle bank operation events
    connection.on('BankDepositUpdated', (depositData) => {
      // Update bank deposits for the specific bank owner
      if (depositData.bankOwnerId === userId) {
        setBankDeposits(prev => ({
          ...prev,
          [depositData.bankTileKey]: depositData.depositAmount
        }));
      }
    });

    connection.on('BankOperationError', (error) => {
      console.error('Bank operation error:', error);
      alert(error); // Show error to user
    });

    // Handle bank interest rate updates
    connection.on('BankInterestRateUpdated', (data) => {
      setBankInterestRates(prev => ({
        ...prev,
        [data.bankTileKey]: data.interestRate
      }));
    });

    // Handle bank info updates (for all players to see changes)
    connection.on('BankInfoUpdated', (data) => {
      setBankInterestRates(prev => ({
        ...prev,
        [data.bankTileKey]: data.interestRate
      }));
      setBankAvailableLoanAmounts(prev => ({
        ...prev,
        [data.bankTileKey]: data.availableLoanAmount
      }));
    });

    // Handle loan updates
    connection.on('PlayerLoansUpdated', (loanData) => {
      // Check if this loan update is for the current user
      if (loanData.playerId && loanData.playerId === userId) {
        setPlayerLoans(loanData.loans);
      } else if (!loanData.playerId) {
        // Backward compatibility: if no playerId specified, assume it's for current user
        setPlayerLoans(loanData);
      }
    });

    // Handle all loans update (for bank owners to see)
    connection.on('AllLoansUpdated', (loansData) => {
      setAllPlayerLoans(loansData);
    });

    connection.on('LoanOperationError', (error) => {
      console.error('Loan operation error:', error);
      alert(error);
    });

    // Handle bank interest income response
    connection.on('BankInterestIncomeInfo', (data) => {
      setBankInterestIncomes(prev => ({
        ...prev,
        [data.bankTileKey]: data.interestIncome
      }));
    });

    // Handle tile upgrade updates
    connection.on('TileUpgradesUpdated', (upgrades) => {
      setTileUpgrades(upgrades);
    });

    // Handle equipment upgrade updates
    connection.on('EquipmentUpgradesUpdated', (upgrades) => {
      setEquipmentUpgrades(upgrades);
    });

    connection.on('UpgradeError', (error) => {
      console.error('Upgrade error:', error);
      alert(error);
    });

    // Handle trade agreement updates
    connection.on('TradeAgreementsUpdated', (agreements) => {
      setTradeAgreements(agreements);
    });

    connection.on('TradeAgreementError', (error) => {
      console.error('Trade agreement error:', error);
      alert(error);
    });

    // Handle tile owners updates
    connection.on('TileOwnersUpdated', (tileOwners) => {
      setGameData(prev => ({
        ...prev,
        tileOwners: tileOwners
      }));
    });

    connection.on('PropertySellError', (error) => {
      console.error('Property sell error:', error);
      alert(error);
    });

    // Handle stock holdings updates
    connection.on('StockHoldingsUpdated', (holdings) => {
      setStockHoldings(holdings);
    });

    connection.on('StockTradingError', (error) => {
      console.error('Stock trading error:', error);
      alert(error);
    });

    // Handle stock trading enabled notification
    connection.on('StockTradingEnabled', (data) => {
      setIsStockTradingEnabled(true);
      console.log('Stock trading enabled:', data.message);
    });

    // Handle economy effects updates
    connection.on('EconomyEffectsUpdated', (effects) => {
      setEconomyEffects(effects);
    });

    // Handle frog updates
    connection.on('FrogUpdated', (frogData) => {
      setFrog(frogData);
    });

    // Handle game events
    connection.on('GameEvent', (eventData) => {
      // Only add personal events if they're for the current user, or non-personal events for everyone
      if (!eventData.isPersonal || (eventData.isPersonal && eventData.targetPlayerId === userId)) {
        setGameEvents(prev => [...prev, {
          id: eventData.id || (Date.now() + Math.random()).toString(), // Use server ID or generate fallback
          type: eventData.type,
          playerId: eventData.playerId,
          playerName: eventData.playerName,
          borrowerName: eventData.borrowerName,
          tileType: eventData.tileType,
          startingBid: eventData.startingBid,
          winningBid: eventData.winningBid,
          operatingCostAmount: eventData.operatingCostAmount,
          routeIncomeAmount: eventData.routeIncomeAmount,
          loanAmount: eventData.loanAmount,
          repayAmount: eventData.repayAmount,
          interestAmount: eventData.interestAmount,
          collectedAmount: eventData.collectedAmount,
          interestRate: eventData.interestRate,
          reason: eventData.reason,
          tileKey: eventData.tileKey,
          upgradeLevel: eventData.upgradeLevel,
          upgradeCost: eventData.upgradeCost,
          sellPrice: eventData.sellPrice,
          agreementId: eventData.agreementId,
          targetId: eventData.targetId,
          targetName: eventData.targetName,
          requesterName: eventData.requesterName,
          partnerName: eventData.partnerName,
          rejectorName: eventData.rejectorName,
          cancellerName: eventData.cancellerName,
          stockPrice: eventData.stockPrice,
          stockCount: eventData.stockCount,
          buybackPrice: eventData.buybackPrice,
          remainingStocks: eventData.remainingStocks,
          acquirerId: eventData.acquirerId,
          acquirerName: eventData.acquirerName,
          effectType: eventData.effectType,
          effectPercentage: eventData.effectPercentage,
          durationMinutes: eventData.durationMinutes,
          tileTypeName: eventData.tileTypeName,
          timestamp: new Date(eventData.timestamp),
          isPersonal: eventData.isPersonal || false,
          targetPlayerId: eventData.targetPlayerId
        }]);
      }
    });

    return () => {
      connection.stop();
    };
  }, [userId, displayName, gameRoomId, navigate]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const deltaX = e.clientX - lastMousePos.x;
      const deltaY = e.clientY - lastMousePos.y;

      setCamera(prev => ({
        ...prev,
        x: prev.x - deltaX,
        y: prev.y - deltaY
      }));

      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleEliminationSpectate = () => {
    setIsSpectating(true);
    setPlayerEliminated(null);
  };

  const handleEliminationReturnHome = () => {
    handleBackToHome();
  };

  const handleCanvasClick = (e) => {
    // Disable interactions if spectating
    if (isSpectating) return;
    
    if (isDragging) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    const worldX = (mouseX + camera.x) / camera.zoom;
    const worldY = (mouseY + camera.y) / camera.zoom;
    
    const tileX = Math.floor(worldX / TILE_SIZE);
    const tileY = Math.floor(worldY / TILE_SIZE);
    
    if (gameData.worldData && 
        tileX >= 0 && tileX < gameData.worldData.width && 
        tileY >= 0 && tileY < gameData.worldData.height) {
      
      const tileIndex = tileY * gameData.worldData.width + tileX;
      const tileType = gameData.worldData.tiles[tileIndex];
      const tileKey = `${tileX},${tileY}`;
      
      if (tileType === 3 || tileType === 4 || tileType === 5 || tileType === 6 || tileType === 7 || tileType === 8 || tileType === 9 || tileType === 10 || tileType === 11 || tileType === 12 || tileType === 13 || tileType === 14) {
        const tileOwner = gameData.tileOwners[tileKey];
        setSelectedTile({
          x: tileX,
          y: tileY,
          type: tileType,
          key: tileKey,
          owner: tileOwner
        });
        
        // If it's a bank and owned by current player, request interest income
        if (tileType === 13 && tileOwner && tileOwner.owner === userId) {
          requestBankInterestIncome(tileKey);
        }
      } else {
        setSelectedTile(null);
      }
    }
  };

  const startAuction = () => {
    // Disable if spectating
    if (isSpectating) return;
    
    if (preparationPeriod.isInPreparation) {
      console.log('Cannot start auctions during preparation period');
      return;
    }
    
    if (selectedTile && connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      const currentPlayer = getCurrentPlayer();
      if (!currentPlayer || currentPlayer.budget < 10000) {
        console.error('Insufficient funds to start auction');
        return;
      }
      connectionRef.current.invoke('StartAuction', gameData.gameRoomId, selectedTile.x, selectedTile.y, userId);
    }
  };

  const placeBid = () => {
    // Disable if spectating
    if (isSpectating) return;
    
    if (bidAmount && connectionRef.current && selectedTile && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      const bid = parseFloat(bidAmount);
      const currentPlayer = getCurrentPlayer();
      
      // Validate bid amount and player budget
      if (!currentPlayer) {
        console.error('Player not found');
        return;
      }
      
      if (currentPlayer.budget < bid) {
        console.error('Insufficient funds to place bid');
        return;
      }
      
      const selectedAuction = getSelectedTileAuction();
      if (!selectedAuction) {
        console.error('No active auction for selected tile');
        return;
      }
      
      if (bid < selectedAuction.highestBid + 1000) {
        console.error('Bid must be at least $1,000 higher than current bid');
        return;
      }
      
      connectionRef.current.invoke('PlaceBid', gameData.gameRoomId, selectedTile.key, userId, bid);
      setBidAmount('');
    }
  };

  const sendChatMessage = (e) => {
    e.preventDefault();
    
    if (!chatInput.trim() || !connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      return;
    }
    
    connectionRef.current.invoke('SendChatMessage', gameData.gameRoomId, userId, displayName, chatInput.trim());
    setChatInput('');
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    
    if (diff < 60000) {
      return 'now';
    } else if (diff < 3600000) {
      return `${Math.floor(diff / 60000)}m ago`;
    } else {
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  const getOwnerDisplayName = (ownerId) => {
    const player = gameData.players.find(p => p.userId === ownerId);
    return player?.displayName || ownerId;
  };

  const getPlayerColor = (playerId) => {
    const player = gameData.players.find(p => p.userId === playerId);
    return player?.color || '#FFFFFF';
  };

  const getCurrentPlayer = () => {
    return gameData.players.find(p => p.userId === userId);
  };

  const canStartAuction = () => {
    if (preparationPeriod.isInPreparation) return false;
    
    const currentPlayer = getCurrentPlayer();
    return currentPlayer && currentPlayer.budget >= 10000;
  };

  const canPlaceBid = () => {
    if (!selectedTile || !bidAmount) return false;
    
    const currentPlayer = getCurrentPlayer();
    const bid = parseFloat(bidAmount);
    const selectedAuction = getSelectedTileAuction();
    
    if (!currentPlayer || !selectedAuction || isNaN(bid)) return false;
    
    return currentPlayer.budget >= bid && bid >= selectedAuction.highestBid + 1000;
  };

  const handleBackToHome = () => {
    // Clear the stored game room when explicitly leaving
    localStorage.removeItem('currentGameRoomId');
    if (connectionRef.current) {
      connectionRef.current.stop();
    }
    navigate('/');
  };

  const handleExecuteAction = async (actionType, targetUserId) => {
    if (!gameRoomId || !connectionRef.current) return;
    try {
      await connectionRef.current.invoke('ExecuteAction', gameRoomId, userId, targetUserId, actionType);
    } catch (e) {
      console.error('Failed to execute action:', e);
    }
  };

  // Use the game renderer hook
  const { centerCameraOnTile } = useGameRenderer({
    canvasRef,
    camera,
    setCamera, // Pass setCamera to the hook
    gameData,
    ships,
    vans,
    roadPaths,
    frog,
    animationTime,
    setAnimationTime,
    onCanvasClick: handleCanvasClick
  });

  // Real-time auction timer update for multiple auctions
  useEffect(() => {
    const intervals = {};
    
    Object.entries(gameData.activeAuctions || {}).forEach(([tileKey, auction]) => {
      if (auction && auction.endTime) {
        intervals[tileKey] = setInterval(() => {
          const endTime = new Date(auction.endTime);
          const now = new Date();
          const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
          
          setAuctionTimers(prev => ({
            ...prev,
            [tileKey]: remaining
          }));
          
          // Clear interval if auction has ended
          if (remaining <= 0) {
            clearInterval(intervals[tileKey]);
            setAuctionTimers(prev => {
              const newTimers = { ...prev };
              delete newTimers[tileKey];
              return newTimers;
            });
          }
        }, 1000);
        
        // Set initial time immediately
        const endTime = new Date(auction.endTime);
        const now = new Date();
        const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
        setAuctionTimers(prev => ({
          ...prev,
          [tileKey]: remaining
        }));
      }
    });
    
    return () => {
      Object.values(intervals).forEach(interval => clearInterval(interval));
    };
  }, [gameData.activeAuctions]);

  // Set default bid amount when auction changes
  useEffect(() => {
    if (selectedTile && isSelectedTileInAuction()) {
      const auction = getSelectedTileAuction();
      if (auction && auction.highestBidder !== userId) {
        const minBid = auction.highestBid + 1000;
        setBidAmount(minBid.toString());
      }
    } else {
      setBidAmount('');
    }
  }, [selectedTile, gameData.activeAuctions, userId]);

  const getAuctionTimeRemaining = (tileKey) => {
    return auctionTimers[tileKey] || 0;
  };

  const isSelectedTileInAuction = () => {
    return selectedTile && gameData.activeAuctions && gameData.activeAuctions[selectedTile.key];
  };

  const getSelectedTileAuction = () => {
    return selectedTile ? gameData.activeAuctions[selectedTile.key] : null;
  };

  const isSelectedTileOwnedByCurrentPlayer = () => {
    return selectedTile && selectedTile.owner && selectedTile.owner.owner === userId;
  };

  // Get all ports owned by the current player
  const getOwnedPorts = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedPorts = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 7) { // Port tile type
          ownedPorts.push({
            key: tileKey,
            x,
            y,
            name: `Port (${x},${y})`
          });
        }
      }
    });
    
    return ownedPorts;
  };

  // Get all farms owned by the current player
  const getOwnedFarms = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedFarms = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 3) { // Farm tile type
          ownedFarms.push({
            key: tileKey,
            x,
            y,
            name: `Farm (${x},${y})`
          });
        }
      }
    });
    
    return ownedFarms;
  };

  // Get all markets owned by the current player
  const getOwnedMarkets = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedMarkets = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 4) { // Market tile type
          ownedMarkets.push({
            key: tileKey,
            x,
            y,
            name: `Market (${x},${y})`
          });
        }
      }
    });
    
    return ownedMarkets;
  };

  // Get all fisheries owned by the current player
  const getOwnedFisheries = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedFisheries = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 6) { // Fishery tile type
          ownedFisheries.push({
            key: tileKey,
            x,
            y,
            name: `Fishery (${x},${y})`
          });
        }
      }
    });
    
    return ownedFisheries;
  };

  // Get all lumber mills owned by the current player
  const getOwnedLumberMills = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedLumberMills = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 10) { // Lumber Mill tile type
          ownedLumberMills.push({
            key: tileKey,
            x,
            y,
            name: `Lumber Mill (${x},${y})`
          });
               }
      }
    });
    
    return ownedLumberMills;
  };

  // Get all construction companies owned by the current player
  const getOwnedConstructionCompanies = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedConstructionCompanies = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 14) { // Construction Company tile type
          ownedConstructionCompanies.push({
            key: tileKey,
            x,
            y,
            name: `Construction Company (${x},${y})`
          });
        }
      }
    });
    
    return ownedConstructionCompanies;
  };

  // Get all mines owned by the current player
  const getOwnedMines = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedMines = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 5) { // Mine tile type
          ownedMines.push({
            key: tileKey,
            x,
            y,
            name: `Mine (${x},${y})`
          });
        }
      }
    });
    
    return ownedMines;
  };

  // Get all factories owned by the current player
  const getOwnedFactories = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedFactories = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 12) { // Factory tile type
          ownedFactories.push({
            key: tileKey,
            x,
            y,
            name: `Factory (${x},${y})`
          });
        }
      }
    });
    
    return ownedFactories;
  };

  // Get all power plants owned by the current player
  const getOwnedPowerPlants = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedPowerPlants = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 11) { // Power Plant tile type
          ownedPowerPlants.push({
            key: tileKey,
            x,
            y,
            name: `Power Plant (${x},${y})`
          });
        }
      }
    });
    
    return ownedPowerPlants;
  };

  // Get all oil wells owned by the current player
  const getOwnedOilWells = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedOilWells = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 9) { // Oil Well tile type
          ownedOilWells.push({
            key: tileKey,
            x,
            y,
            name: `Oil Well (${x},${y})`
          });
        }
      }
    });
    
    return ownedOilWells;
  };

  // Get all oil rigs owned by the current player
  const getOwnedOilRigs = () => {
    if (!gameData.tileOwners || !userId) return [];
    
    const ownedOilRigs = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === userId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileType = gameData.worldData.tiles[tileIndex];
        
        if (tileType === 8) { // Oil Rig tile type
          ownedOilRigs.push({
            key: tileKey,
            x,
            y,
            name: `Oil Rig (${x},${y})`
          });
        }
      }
    });
    
    return ownedOilRigs;
  };

  const toggleShippingRoute = (fromPortKey, toPortKey) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('ToggleShippingRoute', gameData.gameRoomId, userId, fromPortKey, toPortKey);
    }
  };

  const toggleLandRoute = (fromKey, toKey) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('ToggleLandRoute', gameData.gameRoomId, userId, fromKey, toKey);
    }
  };

  const isPortConnected = (fromPortKey, toPortKey) => {
    const routes = shippingRoutes[fromPortKey] || [];
    return routes.includes(toPortKey);
  };

  const isLandConnected = (fromKey, toKey) => {
    const routes = landRoutes[fromKey] || [];
    return routes.includes(toKey);
  };

  const depositToBank = (bankTileKey, amount) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('DepositToBank', gameData.gameRoomId, userId, bankTileKey, amount);
    }
  };

  const withdrawFromBank = (bankTileKey, amount) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('WithdrawFromBank', gameData.gameRoomId, userId, bankTileKey, amount);
    }
  };

  const setBankInterestRate = (bankTileKey, interestRate) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('SetBankInterestRate', gameData.gameRoomId, userId, bankTileKey, interestRate);
    }
  };

  const requestLoan = (bankTileKey, loanAmount) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('RequestLoan', gameData.gameRoomId, userId, bankTileKey, loanAmount);
    }
  };

  const repayLoan = (loanId, repayAmount) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('RepayLoan', gameData.gameRoomId, userId, loanId, repayAmount);
    }
  };

  const getBankDeposit = (bankTileKey) => {
    return bankDeposits[bankTileKey] || 0;
  };

  const getTotalBankDeposits = () => {
    return Object.values(bankDeposits).reduce((total, amount) => total + amount, 0);
  };

  const getBankInterestRate = (bankTileKey) => {
    return bankInterestRates[bankTileKey] || 5.0;
  };

  const getBankAvailableLoanAmount = (bankTileKey) => {
    return bankAvailableLoanAmounts[bankTileKey] || 0;
  };

  const getBankInterestIncome = (bankTileKey) => {
    return bankInterestIncomes[bankTileKey] || 0;
  };

  const requestBankInterestIncome = (bankTileKey) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('GetBankInterestIncome', gameData.gameRoomId, bankTileKey);
    }
  };

  const getPlayerLoans = () => {
    // Return all loans with borrower information
    return allPlayerLoans.length > 0 ? allPlayerLoans : playerLoans;
  };

  const upgradeTile = async (tileKey) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      return connectionRef.current.invoke('UpgradeTile', gameData.gameRoomId, userId, tileKey);
    }
  };

  const upgradeEquipment = async (tileKey) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      return connectionRef.current.invoke('UpgradeEquipment', gameData.gameRoomId, userId, tileKey);
    }
  };

  const getTileUpgradeLevel = (tileKey) => {
    return tileUpgrades[tileKey] || 0;
  };

  const getUpgradeCost = (targetLevel) => {
    const costs = { 1: 25000, 2: 50000, 3: 100000 };
    return costs[targetLevel] || 0;
  };
  const getEquipmentUpgradeLevel = (tileKey) => {
    return equipmentUpgrades[tileKey] || 0;
  }

  const getMaxRoutes = (upgradeLevel) => {
    const routesByLevel = { 0: 3, 1: 4, 2: 5, 3: 6 };
    return routesByLevel[upgradeLevel] || 3;
  };
  const getEquipmentMultiplier = (equipmentLevel) => {
    const multipliers = { 0: 1.0, 1: 1.25, 2: 1.5, 3: 2.0 };
    return multipliers[equipmentLevel] || 1.0;
  };


  // Get operating cost for a tile type
  const getOperatingCost = (tileType) => {
    // Map tile types to their actual operating costs from backend
    const operatingCosts = {
      3: 3000,   // Farm
      4: 2000,   // Market
      5: 5000,   // Mine
      6: 3000,   // Fishery
      7: 2000,   // Port
      8: 8000,   // Oil Rig
      9: 8000,   // Oil Well
      10: 4000,  // Lumber Mill
      11: 5000,  // Power Plant
      12: 5000,  // Factory
      13: 2000,  // Bank
      14: 3000   // Construction Company
    };
    return operatingCosts[tileType] || 0;
  };

  // Get route income for a tile type
  const getRouteIncome = (tileType) => {
    // Map tile types to their actual route incomes from backend
    const routeIncomes = {
      3: 3000,   // Farm
      4: 1000,   // Market
      5: 3500,   // Mine
      6: 3000,   // Fishery
      7: 1000,   // Port
      8: 8500,   // Oil Rig
      9: 8500,   // Oil Well
      10: 5000,  // Lumber Mill
      11: 5000,  // Power Plant
      12: 3500,  // Factory
      14: 3000   // Construction Company
    };
    return routeIncomes[tileType] || 0;
  };

  // Get connected routes count for a tile
  const getConnectedRoutesCount = (tileKey, tileType) => {
    let count = 0;
    
    // Check shipping routes for ports and oil rigs
    if (tileType === 7 || tileType === 8) { // Port or Oil Rig
      if (shippingRoutes[tileKey]) {
        count += shippingRoutes[tileKey].length;
      }
    }
    
    // Check land routes for other tiles
    if (landRoutes[tileKey]) {
      count += landRoutes[tileKey].length;
    }
    
    return count;
  };

  // Get total income for a tile based on connected routes
  const getTileIncome = (tileKey, tileType) => {
    const incomePerRoute = getRouteIncome(tileType);
    const connectedRoutes = getConnectedRoutesCount(tileKey, tileType);
    return incomePerRoute * connectedRoutes;
  };

  const requestTradeAgreement = (targetId) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('RequestTradeAgreement', gameData.gameRoomId, userId, targetId);
    }
  };

  const acceptTradeAgreement = (agreementId) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('AcceptTradeAgreement', gameData.gameRoomId, agreementId, userId);
    }
  };

  const rejectTradeAgreement = (agreementId) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('RejectTradeAgreement', gameData.gameRoomId, agreementId, userId);
    }
  };

  const cancelTradeAgreement = () => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('CancelTradeAgreement', gameData.gameRoomId, userId);
    }
  };

  const sellProperty = (tileKey) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('SellProperty', gameData.gameRoomId, userId, tileKey);
      setSelectedTile(null); // Clear selected tile immediately to hide property management
    }
  };

  const checkCanSellBank = (bankTileKey) => {
    // Check if there are any deposits in this bank from any player
    const totalDepositsInBank = getBankDeposit(bankTileKey);
    if (totalDepositsInBank > 0) {
      return false;
    }

    // Check if there are any active loans from this bank
    const allActiveLoans = getPlayerLoans();
    const hasActiveLoansFromBank = allActiveLoans.some(loan => 
      loan.bankTileKey === bankTileKey && loan.isActive
    );

    return !hasActiveLoansFromBank;
  };

  const buyStock = (targetId) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('BuyStock', gameData.gameRoomId, userId, targetId);
    }
  };

  const buybackStock = (acquirerId) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('BuybackStock', gameData.gameRoomId, userId, acquirerId);
    }
  };

  if (!gameData.worldData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.5rem'
      }}>
        {connectionStatus}
      </div>
    );
  }

  const playerChoicesComponent = (
    <PlayerChoices 
      players={gameData.players}
      currentUserId={userId}
      tradeAgreements={tradeAgreements}
      onRequestTradeAgreement={isSpectating ? () => {} : requestTradeAgreement}
      onCancelTradeAgreement={isSpectating ? () => {} : cancelTradeAgreement}
      stockHoldings={stockHoldings}
      onBuyStock={isSpectating ? () => {} : buyStock}
      onBuybackStock={isSpectating ? () => {} : buybackStock}
      isStockTradingEnabled={isStockTradingEnabled}
      tileOwners={gameData.tileOwners}
      worldData={gameData.worldData}
      onPropertyClick={centerCameraOnTile}
      onExecuteAction={isSpectating ? () => {} : handleExecuteAction}
      activeDebuffs={activeDebuffs}
      containerStyle={isMobile ? { position: 'relative', top: 0, left: 0, width: '100%', height: '100%', borderWidth: 0, borderRadius: 0, boxSizing: 'border-box', overflowY: 'auto' } : {}}
    />
  );

  const eventsFeedComponent = (
    <EventsFeed
      events={gameEvents}
      getPlayerColor={getPlayerColor}
      isChatExpanded={isMobile ? true : isChatExpanded}
      isEventsExpanded={isMobile ? true : isEventsExpanded}
      setIsEventsExpanded={setIsEventsExpanded}
      onAcceptTradeAgreement={isSpectating ? () => {} : acceptTradeAgreement}
      onRejectTradeAgreement={isSpectating ? () => {} : rejectTradeAgreement}
      containerStyle={isMobile ? { position: 'relative', top: 0, left: 0, bottom: 'auto', width: '100%', height: '100%', borderWidth: 0, borderRadius: 0 } : {}}
    />
  );

  const chatPanelComponent = (
    <ChatPanel
      isChatExpanded={isMobile ? true : isChatExpanded}
      setIsChatExpanded={setIsChatExpanded}
      chatMessages={chatMessages}
      chatInput={chatInput}
      setChatInput={setChatInput}
      sendChatMessage={sendChatMessage}
      formatTimestamp={formatTimestamp}
      playersCount={gameData.players.length}
      currentUserId={userId}
      containerStyle={isMobile ? { position: 'relative', top: 0, left: 0, bottom: 'auto', width: '100%', height: '100%', borderWidth: 0, borderRadius: 0 } : {}}
    />
  );

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <canvas
        ref={canvasRef}
        style={{ 
          display: 'block', 
          cursor: isSpectating ? 'default' : (isDragging ? 'grabbing' : 'grab')
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleCanvasClick}
      />

      {/* --- Mobile Only Navigation --- */}
      {isMobile && (
        <>
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              zIndex: 9999,
              background: 'rgba(0, 0, 0, 0.95)',
              border: '2px solid #00FF00',
              color: '#00FF00',
              padding: '0 15px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              borderRadius: '8px',
              fontSize: '1.2em',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.5), 0 0 20px rgba(0, 255, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
              textShadow: '0 0 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.6), 1px 1px 2px rgba(0,0,0,1)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              filter: 'drop-shadow(0 0 6px rgba(0, 255, 0, 0.25))'
            }}
          >
            ☰ Menu
          </button>
          
          {isMobileMenuOpen && (
            <div style={{
              position: 'absolute',
              top: '60px',
              left: '10px',
              right: '10px',
              bottom: '10px',
              zIndex: 9998,
              background: 'rgba(0,0,0,0.95)',
              borderRadius: '8px',
              border: '2px solid #4CAF50',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <div style={{ display: 'flex', background: '#333' }}>
                <button 
                  onClick={() => setActiveMobileTab('actions')}
                  style={{ flex: 1, padding: '10px', background: activeMobileTab === 'actions' ? '#4CAF50' : 'transparent', border: 'none', color: 'white', fontWeight: 'bold' }}
                >
                  Actions
                </button>
                <button 
                  onClick={() => setActiveMobileTab('events')}
                  style={{ flex: 1, padding: '10px', background: activeMobileTab === 'events' ? '#4CAF50' : 'transparent', border: 'none', color: 'white', fontWeight: 'bold' }}
                >
                  Events
                </button>
                <button 
                  onClick={() => setActiveMobileTab('chat')}
                  style={{ flex: 1, padding: '10px', background: activeMobileTab === 'chat' ? '#4CAF50' : 'transparent', border: 'none', color: 'white', fontWeight: 'bold' }}
                >
                  Chat
                </button>
              </div>
              <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                {activeMobileTab === 'actions' && playerChoicesComponent}
                {activeMobileTab === 'events' && eventsFeedComponent}
                {activeMobileTab === 'chat' && chatPanelComponent}
              </div>
            </div>
          )}
        </>
      )}
      
      {/* PlayerChoices Component */}
      {!isMobile && playerChoicesComponent}

      {/* Properties List Component */}
      {!isSpectating && (
        <span />
      )}
      
      {/* Economy Status Component */}
      <EconomyStatus 
        economyEffects={economyEffects} 
        containerStyle={
          isMobile 
            ? { top: '60px', left: '-10px' } 
            : {}
        }
      />
      
      {/* Events Feed Component */}
      {!isMobile && eventsFeedComponent}
      
      {/* Tile Info Component - Hide if spectating */}
      {!isSpectating && (
        <TileInfo
          selectedTile={selectedTile}
          gameData={gameData}
          userId={userId}
          startAuction={startAuction}
          canStartAuction={canStartAuction}
          getCurrentPlayer={getCurrentPlayer}
          getOwnerDisplayName={getOwnerDisplayName}
          getPlayerColor={getPlayerColor}
          requestLoan={requestLoan}
          getBankInterestRate={getBankInterestRate}
          getBankAvailableLoanAmount={getBankAvailableLoanAmount}
          getPlayerLoans={getPlayerLoans}
          repayLoan={repayLoan}
          preparationPeriod={preparationPeriod}
        />
      )}

      {/* Property Management Component - Hide if spectating */}
      {!isSpectating && (
        <PropertyManagement
          selectedTile={selectedTile}
          userId={userId}
          gameData={gameData}
          shippingRoutes={shippingRoutes}
          landRoutes={landRoutes}
          toggleShippingRoute={toggleShippingRoute}
          toggleLandRoute={toggleLandRoute}
          isPortConnected={isPortConnected}
          isLandConnected={isLandConnected}
          getOwnedPorts={getOwnedPorts}
          getOwnedFarms={getOwnedFarms}
          getOwnedMarkets={getOwnedMarkets}
          getOwnedFisheries={getOwnedFisheries}
          getOwnedLumberMills={getOwnedLumberMills}
          getOwnedConstructionCompanies={getOwnedConstructionCompanies}
          getOwnedMines={getOwnedMines}
          getOwnedFactories={getOwnedFactories}
          getOwnedPowerPlants={getOwnedPowerPlants}
          getOwnedOilWells={getOwnedOilWells}
          getOwnedOilRigs={getOwnedOilRigs}
          depositToBank={depositToBank}
          withdrawFromBank={withdrawFromBank}
          getBankDeposit={getBankDeposit}
          getTotalBankDeposits={getTotalBankDeposits}
          getCurrentPlayer={getCurrentPlayer}
          setBankInterestRate={setBankInterestRate}
          getBankInterestRate={getBankInterestRate}
          getPlayerLoans={getPlayerLoans}
          repayLoan={repayLoan}
          getOperatingCost={getOperatingCost}
          getBankInterestIncome={getBankInterestIncome}
          getRouteIncome={getRouteIncome}
          getTileIncome={getTileIncome}
          upgradeTile={upgradeTile}
          upgradeEquipment={upgradeEquipment}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevel}
          getUpgradeCost={getUpgradeCost}
          getMaxRoutes={getMaxRoutes}
          getEquipmentMultiplier={getEquipmentMultiplier}
          tradeAgreements={tradeAgreements}
          sellProperty={sellProperty}
          checkCanSellBank={checkCanSellBank}
          economyEffects={economyEffects}
          frog={frog}
          isMobile={isMobile}
        />
      )}

      {/* Auction Panel Component - Hide if spectating */}
      {!isSpectating && (
        <AuctionPanel
          selectedTile={selectedTile}
          isSelectedTileOwnedByCurrentPlayer={isSelectedTileOwnedByCurrentPlayer}
          getSelectedTileAuction={getSelectedTileAuction}
          getAuctionTimeRemaining={getAuctionTimeRemaining}
          getPlayerColor={getPlayerColor}
          getOwnerDisplayName={getOwnerDisplayName}
          getCurrentPlayer={getCurrentPlayer}
          bidAmount={bidAmount}
          setBidAmount={setBidAmount}
          canPlaceBid={canPlaceBid}
          placeBid={placeBid}
          userId={userId}
          isMobile={isMobile}
        />
      )}

      {/* Chat Panel Component */}
      {!isMobile && chatPanelComponent}

      {/* Game Timer Component */}
      <GameTimer 
        gameTimer={gameTimer}
        preparationPeriod={preparationPeriod}
        isChatExpanded={isChatExpanded}
        winningGoal={winningGoal}
        containerStyle={
          isMobile
            ? { bottom: 'auto', top: '10px', left: '130px' }
            : {}
        }
      />

      {/* Player Eliminated Modal */}
      {playerEliminated && (
        <PlayerEliminatedModal
          eliminatedBy={playerEliminated.eliminatedBy}
          onSpectate={handleEliminationSpectate}
          onReturnHome={handleEliminationReturnHome}
          isBankruptcy={playerEliminated.isBankruptcy}
        />
      )}

      {/* Game Completed Modal */}
      {gameCompleted && (
        <GameCompletedModal
          winner={gameCompleted.winner}
          allPlayers={gameCompleted.allPlayers}
          onRedirectHome={handleBackToHome}
          goalReached={gameCompleted.goalReached}
          winningGoal={gameCompleted.winningGoal}
        />
      )}
    </div>
  );
}